import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Share2, Copy, Send, Loader2, Check, MessageCircle, MessageSquare,
  Twitter, Facebook, Linkedin, Mail, QrCode, ExternalLink, Globe,
} from "lucide-react";
import { toast } from "sonner";
import MediaImage from "./MediaImage";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "@/hooks/platform/useCurrentUser";
import { getOrCreateConversation, useSendMessage } from "@/hooks/platform/useMessaging";

export interface ShareTarget {
  /** Path inside the app, e.g. `/events?event=123`. */
  path: string;
  title: string;
  text?: string;
  /** Optional image URL to show in the preview card */
  previewImage?: string | null;
}

export const absoluteUrl = (path: string) =>
  `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`;

/** Native share sheet with a clipboard fallback — works for signed-out visitors too. */
export async function shareLink({ path, title, text }: ShareTarget) {
  const url = absoluteUrl(path);
  try {
    if (navigator.share) {
      await navigator.share({ title, text: text ?? title, url });
      return;
    }
  } catch {
    /* user dismissed the sheet — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  } catch {
    toast.error("Could not copy the link");
  }
}

interface Match { id: string; username: string; full_name: string | null; avatar_url: string | null }

interface ShareButtonProps extends ShareTarget {
  size?: "sm" | "default" | "icon";
  variant?: "outline" | "ghost" | "secondary" | "default";
  label?: string;
  className?: string;
}

type ShareView = "main" | "qr" | "dm";

const SOCIAL_CHANNELS = [
  {
    id: "whatsapp",
    label: "WhatsApp",
    icon: MessageCircle,
    color: "text-emerald-400 border-emerald-700/40 bg-emerald-950/30 hover:bg-emerald-900/40",
    getUrl: (url: string, title: string) => `https://wa.me/?text=${encodeURIComponent(`${title}\n${url}`)}`,
  },
  {
    id: "twitter",
    label: "X / Twitter",
    icon: Twitter,
    color: "text-sky-400 border-sky-700/40 bg-sky-950/30 hover:bg-sky-900/40",
    getUrl: (url: string, title: string) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
  },
  {
    id: "facebook",
    label: "Facebook",
    icon: Facebook,
    color: "text-blue-400 border-blue-700/40 bg-blue-950/30 hover:bg-blue-900/40",
    getUrl: (url: string) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    icon: Linkedin,
    color: "text-cyan-400 border-cyan-700/40 bg-cyan-950/30 hover:bg-cyan-900/40",
    getUrl: (url: string, title: string) => `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
  },
  {
    id: "telegram",
    label: "Telegram",
    icon: Globe,
    color: "text-indigo-400 border-indigo-700/40 bg-indigo-950/30 hover:bg-indigo-900/40",
    getUrl: (url: string, title: string) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  {
    id: "email",
    label: "Email",
    icon: Mail,
    color: "text-rose-400 border-rose-700/40 bg-rose-950/30 hover:bg-rose-900/40",
    getUrl: (url: string, title: string) => `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${title}\n${url}`)}`,
  },
  {
    id: "sms",
    label: "SMS",
    icon: MessageSquare,
    color: "text-yellow-400 border-yellow-700/40 bg-yellow-950/30 hover:bg-yellow-900/40",
    getUrl: (url: string, title: string) => `sms:?&body=${encodeURIComponent(`${title} ${url}`)}`,
  },
];

/**
 * Premium share dialog: rich preview card, multi-platform sharing grid,
 * QR code view, and in-app DM sender.
 */
const ShareButton = ({ path, title, text, previewImage, size = "sm", variant = "outline", label = "Share", className }: ShareButtonProps) => {
  const { userId } = useCurrentUser();
  const sendMessage = useSendMessage();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<ShareView>("main");
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<Match[]>([]);
  const [searching, setSearching] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [dmNote, setDmNote] = useState("");
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const url = absoluteUrl(path);

  const handleClose = (o: boolean) => {
    setOpen(o);
    if (!o) {
      setQuery("");
      setMatches([]);
      setSentTo(null);
      setView("main");
      setDmNote("");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const openSocial = (getUrl: (url: string, title: string) => string) => {
    const target = getUrl(url, title);
    if (target.startsWith("mailto:") || target.startsWith("sms:")) {
      window.location.href = target;
    } else {
      window.open(target, "_blank", "noopener,noreferrer,width=600,height=500");
    }
  };

  // Draw QR code using canvas (simple placeholder — uses qrcode library if available)
  useEffect(() => {
    if (view !== "qr" || !canvasRef.current) return;
    // Draw simple branded QR placeholder with the URL text
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    canvas.width = 200;
    canvas.height = 200;
    ctx.fillStyle = "#09090b";
    ctx.fillRect(0, 0, 200, 200);
    ctx.fillStyle = "#a855f7";
    ctx.font = "bold 10px monospace";
    ctx.textAlign = "center";
    // We load qrcode dynamically if available
    import("qrcode").then((QRCode) => {
      QRCode.toCanvas(canvas, url, {
        color: { dark: "#ffffff", light: "#09090b" },
        width: 200,
        margin: 2,
      }).catch(() => {
        ctx.fillStyle = "#a855f7";
        ctx.font = "9px monospace";
        ctx.fillText("QR: " + url.slice(0, 30), 100, 100);
      });
    }).catch(() => {
      ctx.fillStyle = "#a855f7";
      ctx.font = "9px monospace";
      ctx.fillText("Scan to open", 100, 100);
    });
  }, [view, url]);

  const search = async (q: string) => {
    setQuery(q);
    const term = q.replace(/^@/, "").trim();
    if (term.length < 2) { setMatches([]); return; }
    setSearching(true);
    const { data } = await supabase
      .from("profiles")
      .select("id, username, full_name, avatar_url")
      .or(`username.ilike.%${term}%,full_name.ilike.%${term}%`)
      .limit(8);
    setMatches((data ?? []).filter((m) => m.id !== userId) as Match[]);
    setSearching(false);
  };

  const sendTo = async (m: Match) => {
    if (!userId) { toast.error("Sign in to send this in a message"); return; }
    try {
      const convo = await getOrCreateConversation(userId, m.id);
      const body = dmNote.trim() ? `${dmNote.trim()}\n\n${title}\n${url}` : `${title}\n${url}`;
      await sendMessage.mutateAsync({
        conversationId: convo.id,
        senderId: userId,
        recipientId: m.id,
        body,
      });
      setSentTo(m.id);
      toast.success(`Sent to @${m.username}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not send the message");
    }
  };

  return (
    <>
      <Button size={size} variant={variant} className={className} onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}>
        <Share2 className={size === "icon" ? "w-4 h-4" : "w-4 h-4 mr-1.5"} />
        {size !== "icon" && label}
      </Button>

      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          className="bg-zinc-950 border-zinc-800/80 text-white max-w-[calc(100vw-1.5rem)] sm:max-w-md p-0 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {/* Subtle gradient top accent */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-purple-500/60 to-transparent" />

          <DialogHeader className="px-5 pt-5 pb-0">
            <DialogTitle className="text-base font-semibold text-white flex items-center gap-2">
              <Share2 className="w-4 h-4 text-purple-400" />
              Share
            </DialogTitle>
          </DialogHeader>

          <div className="px-5 py-4 space-y-4">
            {/* Rich Preview Card */}
            <div className="group rounded-xl border border-zinc-800/70 bg-zinc-900/60 overflow-hidden flex items-center gap-3 p-3 hover:border-zinc-700/70 transition-colors">
              {previewImage ? (
                <MediaImage src={previewImage} alt={title} className="w-14 h-14 rounded-lg object-cover shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-purple-900/60 to-violet-800/40 flex items-center justify-center shrink-0">
                  <ExternalLink className="w-5 h-5 text-purple-300" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white truncate leading-tight">{title}</p>
                {text && <p className="text-xs text-zinc-400 truncate mt-0.5">{text}</p>}
                <p className="text-[10px] text-zinc-600 truncate mt-0.5">{url}</p>
              </div>
              <a href={url} target="_blank" rel="noreferrer noopener" className="shrink-0 text-zinc-500 hover:text-white transition-colors">
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>

            {/* Copy Link Bar */}
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Input
                  readOnly
                  value={url}
                  className="bg-zinc-900/80 border-zinc-800 text-zinc-300 text-xs pr-8 focus-visible:ring-purple-500/40 rounded-xl"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={copyLink}
                className={`shrink-0 border-zinc-700 rounded-xl transition-all duration-200 ${copied ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-400" : "bg-zinc-900 hover:bg-zinc-800 text-white"}`}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
              <Button
                size="sm"
                onClick={() => void shareLink({ path, title, text })}
                className="shrink-0 bg-purple-600 hover:bg-purple-500 rounded-xl"
              >
                <Share2 className="w-4 h-4" />
              </Button>
            </div>

            {/* View Tabs */}
            <div className="flex gap-1 bg-zinc-900/60 border border-zinc-800/50 rounded-xl p-1">
              {(["main", "qr", "dm"] as ShareView[]).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  className={`flex-1 text-xs font-medium py-1.5 rounded-lg transition-all duration-200 ${
                    view === v
                      ? "bg-white text-black"
                      : "text-zinc-400 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {v === "main" ? "Socials" : v === "qr" ? "QR Code" : "Send DM"}
                </button>
              ))}
            </div>

            {/* Main Social Grid */}
            {view === "main" && (
              <div className="grid grid-cols-4 gap-2">
                {SOCIAL_CHANNELS.map((ch) => (
                  <button
                    key={ch.id}
                    type="button"
                    onClick={() => openSocial(ch.getUrl)}
                    className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border text-center transition-all duration-200 hover:scale-105 active:scale-95 ${ch.color}`}
                  >
                    <ch.icon className="w-5 h-5" />
                    <span className="text-[10px] font-medium leading-tight">{ch.label}</span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setView("qr")}
                  className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border border-purple-700/40 bg-purple-950/30 text-purple-400 hover:bg-purple-900/40 transition-all duration-200 hover:scale-105 active:scale-95"
                >
                  <QrCode className="w-5 h-5" />
                  <span className="text-[10px] font-medium leading-tight">QR Code</span>
                </button>
              </div>
            )}

            {/* QR Code View */}
            {view === "qr" && (
              <div className="flex flex-col items-center gap-4 py-2">
                <div className="relative rounded-2xl border border-zinc-800 bg-zinc-900 p-3 shadow-xl shadow-purple-950/20">
                  <canvas ref={canvasRef} className="rounded-xl" />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
                  </div>
                </div>
                <div className="text-center space-y-1">
                  <p className="text-sm font-medium text-white">Scan to open</p>
                  <p className="text-xs text-zinc-500">Point any camera at this QR to visit the link</p>
                </div>
              </div>
            )}

            {/* In-App DM View */}
            {view === "dm" && (
              <div className="space-y-3">
                {userId ? (
                  <>
                    <Input
                      value={query}
                      onChange={(e) => void search(e.target.value)}
                      placeholder="Search @username or name…"
                      className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500 focus-visible:ring-purple-500/40 rounded-xl"
                    />
                    <Input
                      value={dmNote}
                      onChange={(e) => setDmNote(e.target.value.slice(0, 160))}
                      placeholder="Add a personal note… (optional)"
                      className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500 focus-visible:ring-purple-500/40 rounded-xl text-sm"
                    />
                    {searching && (
                      <div className="flex items-center gap-2 text-xs text-zinc-500 px-1">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Searching members…
                      </div>
                    )}
                    <div className="max-h-52 overflow-y-auto custom-scrollbar rounded-xl border border-zinc-800/70 divide-y divide-zinc-900 empty:hidden">
                      {matches.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => void sendTo(m)}
                          className="w-full flex items-center gap-3 px-3.5 py-3 text-left hover:bg-white/5 transition-colors group"
                        >
                          <Avatar className="w-9 h-9 shrink-0">
                            <AvatarImage src={m.avatar_url ?? undefined} />
                            <AvatarFallback className="bg-purple-900/60 text-purple-200 text-xs font-semibold">
                              {(m.username ?? "?")[0]?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium truncate text-white">
                              {m.full_name || m.username}
                            </span>
                            <span className="block text-xs text-zinc-500 truncate">@{m.username}</span>
                          </span>
                          {sentTo === m.id ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-600/40 text-[10px] shrink-0">
                              <Check className="w-3 h-3 mr-1" />Sent
                            </Badge>
                          ) : (
                            <Send className="w-4 h-4 text-zinc-500 group-hover:text-purple-400 transition-colors shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                    {matches.length === 0 && query.length >= 2 && !searching && (
                      <p className="text-xs text-zinc-500 text-center py-2">No members found for "{query}"</p>
                    )}
                  </>
                ) : (
                  <div className="text-center py-6 space-y-2">
                    <MessageCircle className="w-8 h-8 text-zinc-600 mx-auto" />
                    <p className="text-sm text-zinc-400 font-medium">Sign in to send this in a message</p>
                    <p className="text-xs text-zinc-600">Share links directly to any member's inbox</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareButton;
